import React, { useState } from 'react';
import { Modal, Form, Input, message } from 'antd';
import { changeCurrentAccountPassword } from '@/services/merchantAccount';

/** 新密码格式：8-20 位，含大小写字母、数字及特殊字符 */
const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])[A-Za-z\d!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]{8,20}$/;

export interface UserPasswordChangeModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * 商家端修改密码弹窗
 * 表单项：旧密码、新密码、确认密码；校验规则与后端一致
 */
const UserPasswordChangeModal: React.FC<UserPasswordChangeModalProps> = ({
  open,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const res = await changeCurrentAccountPassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
        rePassword: values.rePassword,
      });
      if (res?.code === 200 || res?.success) {
        message.success('密码修改成功');
        form.resetFields();
        onClose();
      } else {
        message.error(res?.message || '密码修改失败');
      }
    } catch (e: any) {
      if (e?.errorFields) {
        // 表单校验失败，不提示
        return;
      }
      const msg =
        e?.response?.data?.message ||
        e?.data?.message ||
        e?.message ||
        '密码修改失败，请重试';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="修改密码"
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      destroyOnClose
      okText="确定"
      cancelText="取消"
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        style={{ marginTop: 16 }}
      >
        <Form.Item
          name="oldPassword"
          label="旧密码"
          rules={[{ required: true, message: '请输入旧密码' }]}
        >
          <Input.Password placeholder="请输入旧密码" autoComplete="current-password" />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label="新密码"
          rules={[
            { required: true, message: '请输入新密码' },
            {
              pattern: PASSWORD_REGEX,
              message:
                '密码须 8-20 位，且包含大小写字母、数字及特殊字符',
            },
          ]}
        >
          <Input.Password placeholder="请输入新密码" autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="rePassword"
          label="确认新密码"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: '请再次输入新密码' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('两次输入的密码不一致'));
              },
            }),
          ]}
        >
          <Input.Password placeholder="请再次输入新密码" autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UserPasswordChangeModal;
